import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  Alert,
} from "@mui/material";
import {
  Analytics,
  Dashboard,
  People,
  Email,
  Settings,
  Security,
  Speed,
  TrendingUp,
  BarChart,
  Close,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import AnalyticsDashboard from "./AnalyticsDashboard";

interface AdminPageProps {
  onBack: () => void;
}

const AdminPage: React.FC<AdminPageProps> = ({ onBack }) => {
  const [showAnalytics, setShowAnalytics] = useState(false);

  const adminFeatures = [
    {
      icon: <Analytics />,
      title: "Analytics Dashboard",
      description:
        "View real-time user analytics, conversion funnels, and performance metrics",
      action: () => setShowAnalytics(true),
    },
    {
      icon: <People />,
      title: "User Management",
      description: "Manage users, view profiles, and handle user data",
      action: () => alert("User Management coming soon!"),
    },
    {
      icon: <Email />,
      title: "Email Campaigns",
      description: "Create and manage email campaigns and newsletters",
      action: () => alert("Email Campaigns coming soon!"),
    },
    {
      icon: <Settings />,
      title: "System Settings",
      description: "Configure application settings and integrations",
      action: () => alert("System Settings coming soon!"),
    },
    {
      icon: <Security />,
      title: "Security & Access",
      description: "Manage security settings and user permissions",
      action: () => alert("Security & Access coming soon!"),
    },
    {
      icon: <Speed />,
      title: "Performance Monitoring",
      description: "Monitor system performance and health metrics",
      action: () => alert("Performance Monitoring coming soon!"),
    },
  ];

  if (showAnalytics) {
    return (
      <Box sx={{ position: "relative" }}>
        <Button
          onClick={() => setShowAnalytics(false)}
          startIcon={<Close />}
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 1,
            bgcolor: "background.paper",
            boxShadow: 2,
          }}
        >
          Back to Admin
        </Button>
        <AnalyticsDashboard />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            variant="h3"
            component="h1"
            sx={{ fontWeight: 700, color: "primary.main" }}
          >
            <Dashboard sx={{ mr: 2, verticalAlign: "middle" }} />
            Admin Dashboard
          </Typography>
          <Button variant="outlined" onClick={onBack} startIcon={<Close />}>
            Close Admin
          </Button>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Manage your Dad2Kid application and monitor performance
        </Typography>
      </Box>

      {/* Quick Stats */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <TrendingUp sx={{ color: "primary.main", mr: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    System Status
                  </Typography>
                </Box>
                <Chip
                  label="Healthy"
                  color="success"
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  All systems operational
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <BarChart sx={{ color: "secondary.main", mr: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    API Status
                  </Typography>
                </Box>
                <Chip
                  label="Online"
                  color="success"
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  Backend running smoothly
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Speed sx={{ color: "info.main", mr: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    Response Time
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                  ~245ms
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Average API response
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <Card sx={{ height: "100%" }}>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Security sx={{ color: "warning.main", mr: 1 }} />
                  <Typography variant="h6" color="text.secondary">
                    Security
                  </Typography>
                </Box>
                <Chip
                  label="Protected"
                  color="success"
                  size="small"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  All security measures active
                </Typography>
              </CardContent>
            </Card>
          </motion.div>
        </Grid>
      </Grid>

      {/* Admin Features */}
      <Grid container spacing={3}>
        {adminFeatures.map((feature, index) => (
          <Grid item xs={12} sm={6} md={4} key={feature.title}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <Card
                sx={{
                  height: "100%",
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: 4,
                  },
                }}
                onClick={feature.action}
              >
                <CardContent>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        color: "white",
                        mr: 2,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    {feature.description}
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ textTransform: "none" }}
                  >
                    Access
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Quick Actions */}
      <Paper sx={{ mt: 4, p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
          Quick Actions
        </Typography>
        <List>
          <ListItem>
            <ListItemIcon>
              <Analytics />
            </ListItemIcon>
            <ListItemText
              primary="View Real-time Analytics"
              secondary="Monitor user behavior and conversion metrics"
            />
            <Button
              variant="contained"
              size="small"
              onClick={() => setShowAnalytics(true)}
            >
              Open Dashboard
            </Button>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon>
              <People />
            </ListItemIcon>
            <ListItemText
              primary="User Management"
              secondary="View and manage user accounts"
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => alert("User Management coming soon!")}
            >
              Manage Users
            </Button>
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemIcon>
              <Settings />
            </ListItemIcon>
            <ListItemText
              primary="System Configuration"
              secondary="Configure application settings and integrations"
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => alert("System Configuration coming soon!")}
            >
              Configure
            </Button>
          </ListItem>
        </List>
      </Paper>

      {/* Info Alert */}
      <Alert severity="info" sx={{ mt: 4 }}>
        <Typography variant="body2">
          <strong>Admin Access:</strong> This is a demo admin panel. In
          production, this would be protected with proper authentication and
          authorization.
        </Typography>
      </Alert>
    </Container>
  );
};

export default AdminPage;

